import { button, div, h3, p, span } from '../../../../scripts/dom-builder.js';
import {
  IMAGE_DIMENSIONS,
  SELECTORS,
  VIEW_TYPES,
} from '../product-tour.constants.js';
import {
  appendLoaderToContainer,
  createExtraSection as createExtraSectionCommon,
  decorateRightCaretSVG,
  extractAltText,
  openModalWithActions as openModalWithActionsCommon,
  sanitizeStepDescription,
  updateImageWithLoader,
} from './common.js';
import formatImagePath from './format-image-path.js';
import ProductTourBaseEnhanced from './product-tour-base.js';

/**
 * Desktop-specific configuration
 */
const DESKTOP_CONFIG = {
  VIEW_TYPE: VIEW_TYPES.DESKTOP,
  SELECTORS: {
    CONTAINER: '.gt_desktop .col-9 .gt_content-container',
    NAV_ITEM: '.gt_nav',
    NAV_BUTTON: '.gt_nav-btn',
    NAV_CONTENT: '.gt_nav-content',
    ACTIVE_CLASS: 'gt_active',
    SECTION_SELECTOR: '.gt_nav',
    PREV_STEP_BUTTON: '.tour-prev',
    NEXT_STEP_BUTTON: '.tour-next',
  },
  CLASSES: {
    NAV: 'gt_nav',
    NAV_ACTIVE: 'gt_active',
    NAV_BUTTON: 'gt_focus gt_nav-btn',
    NAV_CONTENT: 'gt_nav-content',
  },
};

/**
 * Streamlined Desktop implementation of the Product Tour
 * Eliminates duplication by leveraging enhanced base class
 */
class DesktopProductTourStreamlined extends ProductTourBaseEnhanced {
  getViewType() {
    return DESKTOP_CONFIG.VIEW_TYPE;
  }

  getSelectors() {
    return DESKTOP_CONFIG.SELECTORS;
  }

  isValidSection(element) {
    return element
      ? element.classList.contains(DESKTOP_CONFIG.CLASSES.NAV)
      : false;
  }

  closeSection(section) {
    section.classList.remove(DESKTOP_CONFIG.CLASSES.NAV_ACTIVE);
    const content = section.querySelector(this.selectors.NAV_CONTENT);
    const buttonNav = section.querySelector(this.selectors.NAV_BUTTON);

    if (content) {
      content.setAttribute('hidden', '');
      content.removeAttribute('style');
    }

    buttonNav?.setAttribute('aria-expanded', 'false');
  }

  openSection(section) {
    section.classList.add(DESKTOP_CONFIG.CLASSES.NAV_ACTIVE);
    const content = section.querySelector(this.selectors.NAV_CONTENT);
    const buttonNav = section.querySelector(this.selectors.NAV_BUTTON);

    if (content) {
      content.removeAttribute('hidden');
    }

    if (buttonNav) {
      buttonNav.setAttribute('aria-expanded', 'true');
      // Focus for accessibility
      buttonNav.focus();
    }
  }

  /**
   * Update text content in the section
   * @param {Element} section - Section element
   * @param {string} sectionTitle - Section title
   * @param {string} stepDescription - Step description
   * @param {number} stepNumber - Step number
   * @param {number} totalSteps - Total steps in section
   */
  updateTextContent(
    section,
    sectionTitle,
    stepDescription,
    stepNumber,
    totalSteps,
  ) {
    const titleEl = section.querySelector('.gt_nav-btn h3');
    const activeEl = section.querySelector('.gt_nav-text--active');
    const stepIndicator = section.querySelector('.gt_controls p span');
    const stepParagraph = section.querySelector('.gt_controls p');

    if (titleEl) {
      titleEl.textContent = sectionTitle;
    }
    if (activeEl) {
      activeEl.innerHTML = sanitizeStepDescription(stepDescription);
    }
    if (stepIndicator) {
      stepIndicator.textContent = stepNumber;
    }

    // Update the total steps text if provided and different from current
    if (stepParagraph && totalSteps) {
      const stepIndicatorElements = this.createStepIndicator(
        stepNumber,
        totalSteps,
      );
      stepParagraph.innerHTML = '';
      stepIndicatorElements.forEach((element) => {
        if (typeof element === 'string') {
          stepParagraph.appendChild(document.createTextNode(element));
        } else {
          stepParagraph.appendChild(element);
        }
      });
    }
  }

  /**
   * Update step image with loader
   * @param {Element} section - Section element
   * @param {Object} stepItem - Step item data
   */
  updateStepImage(section, stepItem) {
    const imageContainer = section
      .closest('.product-tour')
      .querySelector(this.selectors.CONTAINER);

    if (imageContainer) {
      updateImageWithLoader(
        imageContainer,
        stepItem.screen,
        extractAltText(
          stepItem,
          this.i18next.t('MXP.Section.Step.Image.Text', 'Step image'),
        ),
        IMAGE_DIMENSIONS.DESKTOP_WIDTH,
        IMAGE_DIMENSIONS.MOBILE_WIDTH,
      );
    }
  }

  /**
   * Creates desktop content with image and extra section
   * @param {Element} container - The container element
   * @param {Object} data - The data object
   */
  createDesktopContent(container, data) {
    const imagePath = formatImagePath(
      data.overview_screenshot?.screen?.fileUrl,
    );
    const isPlaceholder = !data.overview_screenshot?.screen?.fileUrl;

    // Create content container
    const contentContainer = div({
      class: 'gt_content-container',
      'data-type': 'image',
    });

    // Use enhanced base class method for image creation
    const picture = this.createOptimizedImage(
      imagePath,
      extractAltText(
        data.overview_screenshot,
        this.i18next.t('MXP.Section.Screenshot.Text', 'Screenshot'),
      ),
      isPlaceholder,
    );

    contentContainer.append(picture);
    appendLoaderToContainer(contentContainer);

    // Create desktop extra section
    const desktopExtra = createExtraSectionCommon(data, true);

    container.append(contentContainer, desktopExtra);
  }

  /**
   * Decorates the overview section for desktop view
   * @param {Object} data - The data object containing overview information
   * @returns {Element} The decorated overview section element
   */
  decorateOverviewSection(data) {
    const startTourLabel = this.i18next.t(
      'MXP.Overview.Section.StartTour.Button.Label',
      'Start Tour',
    );
    return this.createSection({
      data,
      isOverview: true,
      sectionClass: `${DESKTOP_CONFIG.CLASSES.NAV} ${DESKTOP_CONFIG.CLASSES.NAV_ACTIVE}`,
      buttonProps: {
        'aria-expanded': 'true',
        'aria-controls': 'contentDesktop1',
      },
      contentProps: {
        id: 'contentDesktop1',
        'aria-label': this.i18next.t(
          'MXP.Overview.Section.Description.Text',
          'Overview description',
        ),
      },
      contentElements: [
        div(
          { class: 'gt_nav-text' },
          sanitizeStepDescription(data.overview_text),
        ),
        div(
          button(
            {
              class: 'gt_link gt_start',
              'aria-label': startTourLabel,
              disabled: true,
              tabindex: '-1',
              ...this.getAAAttributes(
                this.AA_CONSTANTS.LINKS.START_TOUR,
                this.AA_CONSTANTS.LINKS.OVERVIEW,
              ),
            },
            startTourLabel,
            span(
              { class: 'gt_link-icon gt_link-icon-right' },
              decorateRightCaretSVG(),
            ),
          ),
        ),
      ],
    });
  }

  /**
   * Decorates a section for desktop view
   * @param {Object} data - The section data
   * @param {number} sectionNum - The section number
   * @param {number} stepNum - The step number
   * @param {boolean} isLastSection - Whether the section is the last section
   * @returns {Element} The decorated section element
   */
  decorateSection(data, sectionNum, stepNum = 1, isLastSection = false) {
    return this.decorateSectionBase(data, sectionNum, stepNum, isLastSection);
  }

  /**
   * Get desktop-specific section CSS class
   * @returns {string} Section CSS class
   */
  getSectionClass() {
    return DESKTOP_CONFIG.CLASSES.NAV;
  }

  /**
   * Get desktop-specific content ID prefix
   * @returns {string} Content ID prefix
   */
  getContentIdPrefix() {
    return 'contentDesktop';
  }

  /**
   * Build desktop-specific section configuration
   * @param {Object} data - Section data
   * @param {number} sectionNum - Section number
   * @param {number} stepNum - Step number
   * @param {Object} stepContent - Step content
   * @param {number} totalSteps - Total steps
   * @returns {Object} Section configuration
   */
  buildSectionConfig(data, sectionNum, stepNum, stepContent, totalSteps) {
    return this.buildSectionConfigBase(
      data,
      sectionNum,
      stepNum,
      stepContent,
      totalSteps,
    );
  }

  /**
   * Create desktop-specific content elements
   * @param {Object} _stepItem - Step item data (unused in desktop)
   * @param {string} stepDescription - Step description
   * @param {string} sectionTitle - Section title
   * @param {number} stepNum - Step number
   * @param {number} totalSteps - Total steps
   * @returns {Array} Content elements
   */
  createSectionContentElements(
    _stepItem,
    stepDescription,
    sectionTitle,
    stepNum,
    totalSteps,
    allSteps,
  ) {
    const sizerEls = (allSteps || []).map((step) => {
      return div(
        {
          class: 'gt_nav-text gt_nav-text--sizer',
          'aria-hidden': 'true',
        },
        sanitizeStepDescription(step.description),
      );
    });

    const sourceSizer = sizerEls[stepNum - 1];
    const activeEl = div(
      {
        class: 'gt_nav-text gt_nav-text--active',
        'aria-live': 'polite',
      },
      sourceSizer
        ? sourceSizer.innerHTML
        : sanitizeStepDescription(stepDescription),
    );

    return [
      div({ class: 'gt_text-wrapper' }, ...sizerEls, activeEl),
      this.createNavigationButtons(sectionTitle, stepNum, totalSteps),
    ];
  }

  /**
   * Desktop-specific section creation using base template
   * @param {Object} config - Section configuration
   * @returns {Element} Created section element
   */
  createSection(config) {
    return this.createSectionBase(config);
  }

  /**
   * Create desktop-specific DOM structure
   * @param {Object} sectionAttributes - Section attributes
   * @param {Object} buttonAttributes - Button attributes
   * @param {string} title - Section title
   * @param {Object} contentAttributes - Content attributes
   * @param {Array} contentElements - Content elements
   * @returns {Element} Created DOM element
   */
  createSectionDOM(
    sectionAttributes,
    buttonAttributes,
    title,
    contentAttributes,
    contentElements,
  ) {
    return div(
      sectionAttributes,
      button(
        {
          class: DESKTOP_CONFIG.CLASSES.NAV_BUTTON,
          ...buttonAttributes,
        },
        h3({ class: 'm-0' }, title),
      ),
      div(
        {
          class: DESKTOP_CONFIG.CLASSES.NAV_CONTENT,
          ...contentAttributes,
        },
        ...contentElements,
      ),
    );
  }

  /**
   * Adds navigation listeners to the container for desktop view
   * @param {Element} container - The container element
   * @param {Object} data - The data object
   */
  addNavListeners(container, data) {
    if (!container || !data) {
      return;
    }

    const handleNavClick = (event) => {
      const navButton = event.target.closest(this.selectors.NAV_BUTTON);
      if (!navButton) {
        return;
      }

      const navItem = navButton.closest(this.selectors.NAV_ITEM);
      if (!navItem) {
        return;
      }

      this.handleNavItemClick(container, navItem, data);
    };

    container.addEventListener('click', handleNavClick);
  }

  /**
   * Handles navigation item click logic for desktop view
   * @param {Element} container - The container element
   * @param {Element} navItem - The navigation item element
   * @param {Object} data - The data object
   */
  handleNavItemClick(container, navItem, data) {
    // Hide modal when switching sections
    import('./common.js')
      .then(({ closeModalIfOpen }) => {
        closeModalIfOpen(DESKTOP_CONFIG.VIEW_TYPE);
      })
      .catch(() => {
        // Continue without modal close if import fails
      });

    // Ensure gt_desktop-extra is visible when switching sections
    // (will be hidden again if needed in modal)
    const desktopExtra = document.querySelector('.gt_desktop-extra');
    if (desktopExtra) {
      desktopExtra.style.display = 'block';
    }

    // Close currently active section
    const activeNav = container.querySelector(
      `.${DESKTOP_CONFIG.CLASSES.NAV_ACTIVE}`,
    );
    if (activeNav && activeNav !== navItem) {
      this.closeSection(activeNav);
    }

    // Toggle clicked section
    const isExpanding = !navItem.classList.contains(
      DESKTOP_CONFIG.CLASSES.NAV_ACTIVE,
    );

    if (isExpanding) {
      this.openSection(navItem);
      this.handleSectionContentUpdate(navItem, data);
    } else {
      this.closeSection(navItem);
    }
  }

  /**
   * Handles updating section content and tracking transition
   * @param {Element} navItem - The navigation item element
   * @param {Object} data - The data object
   */
  handleSectionContentUpdate(navItem, data) {
    const sectionIndexAttr = navItem.getAttribute(SELECTORS.DATA_SECTION);
    const sectionIndex = sectionIndexAttr
      ? Number.parseInt(sectionIndexAttr, 10)
      : Number.NaN;
    const stepNumber = Number.parseInt(navItem.dataset.step, 10) || 1;

    if (!Number.isNaN(sectionIndex) && data?.section?.[sectionIndex]) {
      // regular section
      this.updateStep(navItem, data.section[sectionIndex], stepNumber, data);
    } else {
      // Treat as overview ONLY if the element has NO data-section attribute
      const hasDataSectionAttr = navItem.hasAttribute(SELECTORS.DATA_SECTION);
      if (!hasDataSectionAttr && data?.overview_screenshot) {
        // overview (no data-section) -> restore overview content
        this.updateStep(navItem, data, 1, data);
      }
      // otherwise invalid index -> do nothing
    }
  }

  /**
   * Closes all sections except the specified one
   * @param {Element} block - The block element
   * @param {Element} excludeNav - The navigation element to exclude from closing
   */
  closeOtherSections(block, excludeNav) {
    const allNavs = block.querySelectorAll('.gt_nav');
    allNavs.forEach((nav) => {
      if (nav !== excludeNav) {
        this.closeSection(nav);
      }
    });
  }

  /**
   * Attaches start button listeners for desktop view
   * @param {Element} block - The block element
   * @param {Object} data - The data object
   */
  attachStartButtonListeners(block, data) {
    this.attachListenersWithCleanup(block, () => {
      const startButtons = block.querySelectorAll('.gt_nav .gt_start');
      if (!startButtons.length) {
        return [];
      }

      const cleanupFunctions = [];

      startButtons.forEach((startButton) => {
        // Enable the button
        startButton.removeAttribute('disabled');
        startButton.removeAttribute('tabindex');

        const clickHandler = () => {
          const currentNav = startButton.closest('.gt_nav');
          const nextNav = currentNav?.nextElementSibling;

          if (!nextNav || !this.isValidSection(nextNav)) {
            return;
          }

          this.trackSectionTransition(data.section[0]);

          // Close current, open next
          this.closeSection(currentNav);
          this.openSection(nextNav);

          // Update step content for the next section
          this.updateStep(nextNav, data.section[0], 1, data);

          // Close all other sections
          this.closeOtherSections(block, nextNav);
        };

        startButton.addEventListener('click', clickHandler);

        // Return cleanup function for this listener
        cleanupFunctions.push(() => {
          startButton.removeEventListener('click', clickHandler);
        });
      });

      return cleanupFunctions;
    });
  }

  /**
   * Creates the extra section with heading and buttons
   * @param {Object} data - The data object containing title and call_to_action information
   * @returns {Element} The extra section element
   */
  createExtraSection(data) {
    return createExtraSectionCommon(data, true);
  }

  /**
   * Opens the modal with actions for desktop view
   * @param {Object} data - Section data
   * @returns {Element} Created DOM element
   */
  openModalWithActions(data) {
    return openModalWithActionsCommon(data);
  }
}
export default DesktopProductTourStreamlined;
